import React, { useState, useEffect, useRef } from 'react';
import { Box, Avatar, Typography, Paper, IconButton, Menu, MenuItem } from '@mui/material';
import MessageDisplay from './MessageDisplay';
import MessageInput from './MessageInput';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';
import useAlert from '../../alert/useAlert';


const ChatScreen = ( {phoneNumber, userDetails, textMessages, insertTextMessage, updateTextMessage, deleteTextMessage, selectedContact, onClose } ) => {
    // const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const [repliedMessage, setRepliedMessage] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const { setAlert } = useAlert();

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleCloseChat = () => {
        handleMenuClose();
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    useEffect(() => {
        if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [textMessages, selectedContact]);

    // Deliver the therapist's reply to the patient's WhatsApp via the health-intent bot backend
    // (the Meta Cloud API + token live there — no secrets in the browser). selectedContact is the
    // chat_room_id, i.e. the patient's phone number, which is exactly the `to` the backend expects.
    const sendWhatsappMessage = async (messageText) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_WHATSAPP_BACKEND_URL}/whatsapp/send-message`,
                { message: messageText, to_source: selectedContact },
                { headers: { 'Content-Type': 'application/json' } }
            );
            return true;
        } catch (error) {
            console.error('Error sending WhatsApp message to patient:', error);
            return false;
        }
    };

    // Therapist reply: (1) store it in wa_messages so it shows in this inbox, then (2) deliver it to
    // the patient on WhatsApp. Meta only allows a free-form reply within 24h of the patient's last
    // message; outside that window the send fails and we warn the therapist (the message is still
    // saved here regardless).
    const handleSendMessage = async (newMessage) => {
        if (repliedMessage) {
            const target = textMessages.find((msg) => msg.id === repliedMessage.id);
            if (target?.ispinned) {
                await updateTextMessage(repliedMessage.id, false); // replying unpins the message
            }
        }
        await insertTextMessage(selectedContact, newMessage, repliedMessage?.id ?? null);
        const delivered = await sendWhatsappMessage(newMessage);
        if (!delivered) {
            setAlert(
                "Saved to the chat, but couldn't be delivered to the patient's WhatsApp (they may be outside WhatsApp's 24-hour reply window).",
                'warning'
            );
        }
    };

    const handleReply = (message, name) => {
        setRepliedMessage({ ...message, name });
    };

    const handleCancelReply = () => {
        setRepliedMessage(null);
    };

    // The conversation is keyed by the patient's phone number (wa_messages.chat_room_id).
    const getContactTitle = () => selectedContact || '';

    // Human-readable label for who sent a message.
    const getSenderLabel = (msg) => {
        if (msg.sender_type === 'therapist') return msg.sender_name || userDetails?.name || 'You';
        if (msg.sender_type === 'ai') return 'Virtual Assistant';
        return 'Patient';
    };

    return (
        <Grid container spacing={5} sx={{ paddingBottom: '70px' }}>
            <Grid size={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" sx={{ backgroundColor: '#FFFFFF', padding: '10px 20px', borderRadius: '20px', }}>
                    <IconButton
                        size="large"
                        edge="start"
                        onClick={() => {}}
                    >
                        <AccountCircle sx={{ fontSize: '40px', marginRight: '10px' }}/>
                    </IconButton>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'bold', fontSize: '18px' }}>
                        {getContactTitle()}
                    </Typography>
                    <IconButton
                        size="large"
                        edge="end"
                        onClick={handleMenuOpen}
                    >
                        <MoreVertIcon sx={{ fontSize: '30px', marginLeft: '10px' }}/>
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={handleCloseChat}>Close</MenuItem>
                    </Menu>
                </Stack>
            </Grid>
            <Grid size={12} sx={{ height: 'calc(100vh - 360px)', minHeight: '320px', overflowY: 'auto', overflowX: 'hidden', padding: '0px 20px' }}>
                {textMessages
                .filter((message) => message.chat_room_id === selectedContact)
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                .map((msg) => (
                    <MessageDisplay
                    key={msg.id}
                    message={msg}
                    alignRight={msg.sender_type === 'patient'}
                    senderLabel={getSenderLabel(msg)}
                    onReply={handleReply}
                    textMessages={textMessages}
                    deleteTextMessage={deleteTextMessage}
                    updateTextMessage={updateTextMessage}
                    />
                ))}
                {/* {isTyping && (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{`${selectedContact} is typing...`}</Typography>
                )} */}
                <div ref={messagesEndRef} />
            </Grid>
            <Grid size={12}>
                <MessageInput onSendMessage={handleSendMessage} repliedMessage={repliedMessage} onCancelReply={handleCancelReply} />
            </Grid>
        </Grid>
    );
    };

    export default ChatScreen;
